import React, { useEffect, useState } from "react";
import { History } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Select } from "@/components/DataTable";

const ACTION_TONE = (a) => {
  if (a.includes("Failed") || a.includes("Override") || a.includes("archived")) return "bg-red-50 text-red-600";
  if (a.includes("Frequency") || a.includes("reassigned") || a.includes("Schedule") || a.includes("Status")) return "bg-amber-50 text-amber-600";
  if (a.includes("created") || a.includes("login")) return "bg-emerald-50 text-emerald-600";
  return "bg-slate-100 text-slate-500";
};

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordType, setRecordType] = useState("");
  const [action, setAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (recordType) p.set("record_type", recordType);
      if (action) p.set("action", action);
      const { data } = await api.get(`/audit-logs?${p}`); setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [recordType, action]);

  const types = ["User", "Client", "Site", "Contract", "JobTemplate", "ScheduledVisit", "Role", "Leave", "Report"];
  const actions = ["User login", "Failed login", "Record created", "Record edited", "Record archived", "Schedule changed", "Consultant reassigned", "Frequency changed", "Status changed", "Permission changed", "Override used", "Export generated"];

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Complete history of security, scheduling, financial and record changes" icon={History} />
      <div className="flex gap-2 mb-4">
        <Select value={recordType} onChange={(e) => setRecordType(e.target.value)} className="max-w-[180px]"><option value="">All record types</option>{types.map((t) => <option key={t}>{t}</option>)}</Select>
        <Select value={action} onChange={(e) => setAction(e.target.value)} className="max-w-[200px]"><option value="">All actions</option>{actions.map((a) => <option key={a}>{a}</option>)}</Select>
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <EmptyState icon={History} title="No audit entries" /> : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {rows.map((e) => (
            <div key={e.id} data-testid={`audit-${e.id}`} className="flex items-start gap-3 px-4 py-3">
              <span className={`text-[11px] font-semibold px-2 py-1 rounded shrink-0 ${ACTION_TONE(e.action)}`}>{e.action}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-700">
                  <span className="font-medium">{e.user_name || "system"}</span>
                  <span className="text-slate-400"> · {e.record_type}</span>
                  {e.reason && <span className="text-slate-500"> — {e.reason}</span>}
                </div>
                {(e.original_value != null || e.new_value != null) && (
                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {e.original_value != null && <>from <span className="text-slate-500">{String(e.original_value)}</span> </>}
                    {e.new_value != null && <>to <span className="text-slate-600">{String(e.new_value)}</span></>}
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-400 shrink-0 text-right">{fmtDateTime(e.timestamp)}{e.ip_address && <div className="text-[10px]">{e.ip_address}</div>}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
